import { CURVE_COLOR } from 'constants/Curves';
import { RequestStatus } from 'constants/RequestStatus';

import { createSelector } from '@reduxjs/toolkit';
import { PlanPredictCurvesRequestPayload, PlanPredictCurvesResponsePayload } from 'api/curves';

import {
  AxisIntervalBorderNameType,
  EmissionYAxisType,
} from 'app/components/containers/layout/ConfigTabs/controls/AxisMinMaxInterval/types';
import {
  selectEmissionAxisIntervalBorderValue,
  selectXAxisMaxValue,
  selectXAxisMinValue,
  selectYAxisMinValue,
} from 'app/components/containers/layout/ConfigTabs/PlannerControl/ProductionCurvesOptions/selectors';
import {
  selectActiveCurveEmissoinValues,
  selectAggregateProductionValue,
  selectCurveEmissoinSelectionValues,
  selectCurveSelectionValues,
  selectEmissionsCurveSelectionItemValue,
  selectIsErrorsEnabled,
  selectIsPredictionEnabled,
  selectPredictionModelsValues,
  selectProductionCurveSelectionItemValue,
} from 'app/components/containers/layout/ConfigTabs/PlannerControl/ProductionCurvesSettings/selectors';
import { selectAllPlannedWellParams } from 'app/components/containers/layout/ConfigTabs/PlannerFilter/WellParametersSection/selectors';
import { MONTH_NUMBER_DATA_KEY } from 'app/components/containers/widgets/ProductionCurves/constants';
import {
  getChartLineColorByProdKey,
  getChartXAxisMaxValue,
  getChartYAxisMaxValue,
  getVisibleChartData,
} from 'app/components/containers/widgets/ProductionCurves/utils';
import {
  selectReadyToPlanWellIds,
  selectVisibleWellIds,
  selectVisibleWellsSettings,
} from 'app/components/pages/WellPlannerPage/PlanMapWidget/WellControl/selectors';
import {
  AGREGATED_EMISSIONS_KEYS,
  AGREGATED_ERRORS_EMISSIONS_KEYS,
  ALL_AGREGATED_EMISSIONS_KEYS,
  CurveLabels,
  CurveType,
  EmissionCurvesType,
  WidgetCurveType,
} from 'types/Curve';
import { PlanModelType } from 'types/PlanPredictModel';
import { RootState } from 'types/RootState';

import { initialState } from './slice';
import {
  ECurvesType,
  PLAN_CURVE_CHART_KEY,
  PLAN_CURVE_DATA_KEY,
  PLAN_CURVE_EMISSION_DATA_KEY,
  PlanProdChartItem,
  PlanProdCurvesWidgetState,
} from './types';
import {
  generatePlanChartItemByDataKey,
  generatePlanChartKeys,
  getPCurveByDataKey,
  getPlannedWellDataKey,
  getValidatedValue,
  getVisibleCurvesLines,
  parseAggregationName,
} from './utils';

const selectPlanProdCurvesWidgetDomain = (state: RootState) => state?.planProdCurvesWidget || initialState;

const selectVisiblePlanProdCurvesWidgetDomain = createSelector(
  [selectPlanProdCurvesWidgetDomain, selectVisibleWellIds],
  (widgetState, visibleWellIds) => {
    return Object.keys(widgetState).reduce<PlanProdCurvesWidgetState>((acc, wellIdString) => {
      const wellId = Number(wellIdString);
      if (visibleWellIds.includes(wellId)) {
        acc[wellId] = widgetState[wellId];
      }

      return acc;
    }, {});
  },
);

export const selectIsAggregateProductionAvailable = createSelector(
  [selectVisiblePlanProdCurvesWidgetDomain],
  (widgetState) =>
    Object.keys(widgetState).filter(
      (wellId) => widgetState[wellId as unknown as number].status === RequestStatus.SUCCESS,
    ).length > 1,
);

export const selectRequestPayloadsToLoadPredictedCurves = createSelector(
  [selectPlanProdCurvesWidgetDomain, selectReadyToPlanWellIds, selectAllPlannedWellParams],
  (widgetState, plannedWellIds, plannedWellParams) => {
    return plannedWellIds
      .filter((id) => {
        return widgetState[id]
          ? getPlannedWellDataKey(plannedWellParams[id]) !== widgetState[id].dataKey
          : Boolean(plannedWellParams[id]);
      })
      .map((id) => {
        return {
          wellId: id,
          ...plannedWellParams[id],
        } as PlanPredictCurvesRequestPayload & { wellId: number };
      });
  },
);
export const selectIsChartDataWithErrors = createSelector(
  [selectPlanProdCurvesWidgetDomain, selectReadyToPlanWellIds, selectAllPlannedWellParams],
  (widgetState, plannedWellIds, plannedWellParams) => {
    return plannedWellIds
      .filter((id) => {
        return widgetState[id]
          ? getPlannedWellDataKey(plannedWellParams[id]) !== widgetState[id].dataKey
          : Boolean(plannedWellParams[id]);
      })
      .map((id) => {
        return {
          wellId: id,
          ...plannedWellParams[id],
        } as PlanPredictCurvesRequestPayload & { wellId: number };
      });
  },
);

export const selectIsAggregateProductionEnabled = createSelector(
  [selectIsAggregateProductionAvailable, selectAggregateProductionValue],
  (isAggregateProductionAvailable, isAggregateProductionChecked) =>
    isAggregateProductionAvailable && isAggregateProductionChecked,
);

const selectIndividualChartData = createSelector([selectVisiblePlanProdCurvesWidgetDomain], (widgetState) => {
  return Object.keys(widgetState).reduce((acc, wellId) => {
    const wellChartData = widgetState[wellId as unknown as number][ECurvesType.PROD_CURVES].data?.map((wellDataItem) =>
      generatePlanChartItemByDataKey(wellDataItem, wellId as unknown as number),
    );

    return wellChartData
      ? wellChartData.map((chartData, index) => ({
          ...chartData,
          ...acc[index],
        }))
      : acc;
  }, [] as PlanProdChartItem[]);
});

const selectAggregatedChartData = createSelector([selectVisiblePlanProdCurvesWidgetDomain], (widgetState) => {
  return Object.keys(widgetState).reduce((acc, wellId) => {
    widgetState[wellId as unknown as number][ECurvesType.PROD_CURVES].data?.forEach((wellDataItem, index) => {
      Object.keys(wellDataItem).forEach((wellDataItemKey) => {
        const value = wellDataItem[wellDataItemKey as PLAN_CURVE_DATA_KEY];
        if (wellDataItemKey !== MONTH_NUMBER_DATA_KEY) {
          acc[index] = {
            ...acc[index],
            [wellDataItemKey]:
              value === null
                ? acc[index]?.[wellDataItemKey as PLAN_CURVE_DATA_KEY] ?? value
                : (acc[index]?.[wellDataItemKey as PLAN_CURVE_DATA_KEY] ?? 0) + value,
          };
        } else {
          acc[index] = {
            ...acc[index],
            [wellDataItemKey as PLAN_CURVE_DATA_KEY]: value,
          };
        }
      });

      return wellDataItem;
    });

    return acc;
  }, [] as PlanProdChartItem[]);
});

const selectAggregatedEmissionChartData = createSelector([selectVisiblePlanProdCurvesWidgetDomain], (widgetState) => {
  return Object.keys(widgetState).reduce((acc, wellId) => {
    widgetState[wellId as unknown as number][ECurvesType.EMISSION_CURVES].data?.forEach((wellDataItem, index) => {
      Object.keys(wellDataItem).forEach((wellDataItemKey) => {
        const value = wellDataItem[wellDataItemKey as PLAN_CURVE_EMISSION_DATA_KEY];
        if (wellDataItemKey !== MONTH_NUMBER_DATA_KEY) {
          const aggregationName = parseAggregationName(wellDataItemKey);
          acc[index] = {
            ...acc[index],
            [aggregationName]:
              value === null ? acc[index]?.[aggregationName] ?? value : (acc[index]?.[aggregationName] ?? 0) + value,
          };
        } else {
          acc[index] = {
            ...acc[index],
            [wellDataItemKey as PLAN_CURVE_EMISSION_DATA_KEY]: value,
          };
        }
      });

      return wellDataItem;
    });

    return acc;
  }, [] as PlanProdChartItem[]);
});

export const selectChartData = createSelector(
  [selectIsAggregateProductionEnabled, selectIndividualChartData, selectAggregatedChartData],
  (isAggregateProductionEnabled, individualChartData, aggregatedChartData) => {
    return isAggregateProductionEnabled ? aggregatedChartData : individualChartData;
  },
);

export const selectIsChartDataLoading = createSelector([selectPlanProdCurvesWidgetDomain], (widgetState) => {
  return Object.keys(widgetState).some(
    (wellId) => widgetState[wellId as unknown as number].status === RequestStatus.LOADING,
  );
});

export const selectIsChartDataLoaded = createSelector([selectPlanProdCurvesWidgetDomain], (widgetState) => {
  const wellIds = Object.keys(widgetState);

  return (
    wellIds.every((wellId) => widgetState[wellId as unknown as number].status !== RequestStatus.LOADING) &&
    wellIds.length > 0
  );
});

export const selectPlanProdCurvesError = createSelector([selectPlanProdCurvesWidgetDomain], (widgetState) => {
  const widgetIdWithError = Object.keys(widgetState).find((wellId) => widgetState[wellId as unknown as number].error);

  return widgetIdWithError ? widgetState[widgetIdWithError as unknown as number].error : null;
});

export const selectEmissionsVisibleLegendItems = createSelector(
  [
    (state: RootState) => selectProductionCurveSelectionItemValue(state, CurveType.LIQUID),
    (state: RootState) => selectProductionCurveSelectionItemValue(state, CurveType.GAS),
    (state: RootState) => selectProductionCurveSelectionItemValue(state, CurveType.WATER),
    (state: RootState) => selectEmissionsCurveSelectionItemValue(state, EmissionCurvesType.CH4CO2E),
    (state: RootState) => selectEmissionsCurveSelectionItemValue(state, EmissionCurvesType.CO2),
    (state: RootState) => selectEmissionsCurveSelectionItemValue(state, EmissionCurvesType.N2OCO2E),
    (state: RootState, widgetCurveType: WidgetCurveType) => widgetCurveType,
  ],
  (
    liquidCurveEnabled,
    gasCurveEnabled,
    waterCurveEnabled,
    ch4co2eCurveEnabled,
    co2CurveEnabled,
    n2oco2eCurveEnabled,
    widgetCurveType,
  ) =>
    widgetCurveType === WidgetCurveType.PRODUCTION
      ? ([
          liquidCurveEnabled && { label: CurveLabels.PREDICTED_LIQUID, isSolidLine: true, color: CURVE_COLOR.liquid },
          gasCurveEnabled && { label: CurveLabels.PREDICTED_GAS, isSolidLine: true, color: CURVE_COLOR.gas },
          waterCurveEnabled && { label: CurveLabels.PREDICTED_WATER, isSolidLine: true, color: CURVE_COLOR.water },
        ].filter(Boolean) as {
          label: string;
          isSolidLine: boolean;
          color: (typeof CURVE_COLOR)[keyof typeof CURVE_COLOR];
        }[])
      : ([
          ch4co2eCurveEnabled && { label: CurveLabels.CH4_CO2E, isSolidLine: true, color: CURVE_COLOR.ch4Co2e },
          co2CurveEnabled && { label: CurveLabels.CO2, isSolidLine: true, color: CURVE_COLOR.co2 },
          n2oco2eCurveEnabled && { label: CurveLabels.N2O_CO2E, isSolidLine: true, color: CURVE_COLOR.n2oCo2e },
        ].filter(Boolean) as {
          label: string;
          isSolidLine: boolean;
          color: (typeof CURVE_COLOR)[keyof typeof CURVE_COLOR];
        }[]),
);

const selectSelectedProdCurvesDataKeysWithWellIds = createSelector(
  [selectCurveSelectionValues, selectPredictionModelsValues, selectReadyToPlanWellIds, selectVisibleWellsSettings],
  (curveSelectionValues, predictionModelsValues, wellIds, visibleWells) => {
    const selectedCurves = Object.keys(curveSelectionValues).filter(
      (curveType) => curveSelectionValues[curveType as CurveType],
    ) as CurveType[];
    const selectedModels = Object.keys(predictionModelsValues) as PlanModelType[];
    const visibleWellsIds = visibleWells.map((well) => well.id);

    const curvesDataKeys = wellIds
      .filter((wellId) => visibleWellsIds.includes(wellId))
      .reduce((acc, wellId) => {
        return [
          ...acc,
          ...selectedCurves.reduce((acc, curve) => {
            return [
              ...acc,
              ...selectedModels.reduce((acc, model) => {
                const { general, errors } = predictionModelsValues[model];

                return [...acc, ...generatePlanChartKeys(curve, model, wellId, general, errors)];
              }, [] as PLAN_CURVE_CHART_KEY[]),
            ];
          }, [] as PLAN_CURVE_CHART_KEY[]),
        ];
      }, [] as PLAN_CURVE_CHART_KEY[]);

    return curvesDataKeys;
  },
);

const selectSelectedEmisCurvesDataKeysWithWellIds = createSelector(
  [
    selectCurveEmissoinSelectionValues,
    selectPredictionModelsValues,
    selectReadyToPlanWellIds,
    selectVisibleWellsSettings,
  ],
  (curveSelectionValues, predictionModelsValues, wellIds, visibleWells) => {
    const selectedCurves = Object.keys(curveSelectionValues).filter(
      (curveType) => curveSelectionValues[curveType as EmissionCurvesType],
    ) as CurveType[];
    const selectedModels = Object.keys(predictionModelsValues) as PlanModelType[];
    const visibleWellsIds = visibleWells.map((well) => well.id);

    const curvesDataKeys = wellIds
      .filter((wellId) => visibleWellsIds.includes(wellId))
      .reduce((acc, wellId) => {
        return [
          ...acc,
          ...selectedCurves.reduce((acc, curve) => {
            return [
              ...acc,
              ...selectedModels.reduce((acc, model) => {
                const { general, errors } = predictionModelsValues[model];

                return [...acc, ...generatePlanChartKeys(curve, model, wellId, general, errors)];
              }, [] as PLAN_CURVE_CHART_KEY[]),
            ];
          }, [] as PLAN_CURVE_CHART_KEY[]),
        ];
      }, [] as PLAN_CURVE_CHART_KEY[]);

    return curvesDataKeys;
  },
);

export const selectSelectedProdCurvesDataKeys = createSelector(
  [selectCurveSelectionValues, selectPredictionModelsValues],
  (curveSelectionValues, predictionModelsValues) => {
    const selectedCurves = Object.keys(curveSelectionValues).filter(
      (curveType) => curveSelectionValues[curveType as CurveType],
    ) as CurveType[];
    const selectedModels = Object.keys(predictionModelsValues) as PlanModelType[];

    const curvesDataKeys = selectedCurves.reduce((acc, curve) => {
      return [
        ...acc,
        ...selectedModels.reduce((acc, model) => {
          const { general, errors } = predictionModelsValues[model];

          return [...acc, ...generatePlanChartKeys(curve, model, null, general, errors)];
        }, [] as PLAN_CURVE_CHART_KEY[]),
      ];
    }, [] as PLAN_CURVE_CHART_KEY[]);

    return curvesDataKeys;
  },
);
export const selectSelectedEmissionCurvesDataKeys = createSelector(
  [selectCurveEmissoinSelectionValues, selectPredictionModelsValues],
  (curveSelectionValues, predictionModelsValues) => {
    const selectedCurves = Object.keys(curveSelectionValues).filter(
      (curveType) => curveSelectionValues[curveType as EmissionCurvesType],
    ) as CurveType[];
    const selectedModels = Object.keys(predictionModelsValues) as PlanModelType[];

    const curvesDataKeys = selectedCurves.reduce((acc, curve) => {
      return [
        ...acc,
        ...selectedModels.reduce((acc, model) => {
          const { general, errors } = predictionModelsValues[model];

          return [...acc, ...generatePlanChartKeys(curve, model, null, general, errors)];
        }, [] as PLAN_CURVE_CHART_KEY[]),
      ];
    }, [] as PLAN_CURVE_CHART_KEY[]);

    return curvesDataKeys;
  },
);

const selectSelectedAggregatedProdCurvesDataKeys = createSelector(
  [selectCurveSelectionValues, selectPredictionModelsValues],
  (curveSelectionValues, predictionModelsValues) => {
    const selectedCurves = Object.keys(curveSelectionValues).filter(
      (curveType) => curveSelectionValues[curveType as CurveType],
    ) as CurveType[];
    const selectedModels = Object.keys(predictionModelsValues) as PlanModelType[];

    const curvesDataKeys = selectedCurves.reduce((acc, curve) => {
      return [
        ...acc,
        ...selectedModels.reduce((acc, model) => {
          const { general, errors } = predictionModelsValues[model];

          return [...acc, ...generatePlanChartKeys(curve, model, null, general, errors)];
        }, [] as PLAN_CURVE_CHART_KEY[]),
      ];
    }, [] as PLAN_CURVE_CHART_KEY[]);

    return curvesDataKeys;
  },
);

export const selectLinesProps = createSelector(
  [
    selectIsAggregateProductionEnabled,
    selectIsErrorsEnabled,
    selectSelectedProdCurvesDataKeysWithWellIds,
    selectSelectedEmisCurvesDataKeysWithWellIds,
    selectSelectedAggregatedProdCurvesDataKeys,
    selectActiveCurveEmissoinValues,
    (state: RootState, widgetCurveType: WidgetCurveType) => widgetCurveType,
    selectIsPredictionEnabled,
  ],
  (
    isAggregateProductionEnabled,
    IsErrorsEnabled,
    selectedProdKeys,
    selectedEmissionKeys,
    selectedAggregatedProdKeys,
    curveSelectionValues,
    widgetCurveType,
    isPlanPredictionEnabled,
  ) => {
    return (
      widgetCurveType === WidgetCurveType.PRODUCTION
        ? isAggregateProductionEnabled
          ? selectedAggregatedProdKeys
          : selectedProdKeys
        : isAggregateProductionEnabled
        ? isPlanPredictionEnabled
          ? IsErrorsEnabled
            ? getVisibleCurvesLines(ALL_AGREGATED_EMISSIONS_KEYS, curveSelectionValues)
            : getVisibleCurvesLines(AGREGATED_EMISSIONS_KEYS, curveSelectionValues)
          : IsErrorsEnabled
          ? getVisibleCurvesLines(AGREGATED_ERRORS_EMISSIONS_KEYS, curveSelectionValues)
          : []
        : selectedEmissionKeys
    ).map((key: PLAN_CURVE_CHART_KEY) => ({
      dataKey: key,
      stroke: getChartLineColorByProdKey(key),
      strokeDasharray: getPCurveByDataKey(key) ? '4' : undefined,
    }));
  },
);

export const selectVisibleChartData = createSelector(
  [
    selectChartData,
    selectIsAggregateProductionEnabled,
    selectSelectedProdCurvesDataKeysWithWellIds,
    selectSelectedAggregatedProdCurvesDataKeys,
    selectXAxisMinValue,
    selectXAxisMaxValue,
    selectYAxisMinValue,
  ],
  (data, isAggregateProductionEnabled, selectedProdKeys, selectedAggregatedProdKeys, minX, maxX, valueMin) => {
    return getVisibleChartData(
      data,
      isAggregateProductionEnabled ? selectedAggregatedProdKeys : selectedProdKeys,
      minX,
      maxX,
      valueMin,
    ).visibleData;
  },
);

export const selectVisibleEmissionChartData = createSelector(
  [
    selectPlanProdCurvesWidgetDomain,
    selectVisibleWellsSettings,
    selectXAxisMinValue,
    selectXAxisMaxValue,
    (state: RootState) => state,
    selectIsAggregateProductionEnabled,
    selectAggregatedEmissionChartData,
  ],
  (widgetState, visibleWells, minX, maxX, state, isAggregateProductionEnabled, aggregatedChartData) => {
    const minCh4co2Y = selectEmissionAxisIntervalBorderValue(
      state,
      EmissionYAxisType.CH4_CO2,
      AxisIntervalBorderNameType.MIN,
    );
    const maxCh4co2Y = selectEmissionAxisIntervalBorderValue(
      state,
      EmissionYAxisType.CH4_CO2,
      AxisIntervalBorderNameType.MAX,
    );
    const minNo2Y = selectEmissionAxisIntervalBorderValue(state, EmissionYAxisType.N2O, AxisIntervalBorderNameType.MIN);
    const maxNo2Y = selectEmissionAxisIntervalBorderValue(state, EmissionYAxisType.N2O, AxisIntervalBorderNameType.MAX);

    const chartData = isAggregateProductionEnabled
      ? aggregatedChartData
      : Object.keys(widgetState).reduce<any>((acc, curr) => {
          if (widgetState[Number(curr)][ECurvesType.EMISSION_CURVES].data) {
            return widgetState[Number(curr)][ECurvesType.EMISSION_CURVES].data?.map((chartItem, i) => {
              const chartObject = Object.keys(chartItem).reduce((prevItem, keyItem) => {
                if (visibleWells.some((well) => keyItem.includes(`_${well.id}`))) {
                  return { ...prevItem, [`${keyItem}`]: chartItem[keyItem], monthNumber: i };
                } else {
                  return { ...prevItem, monthNumber: i };
                }
              }, {});

              return {
                ...acc[i],
                ...chartObject,
              };
            });
          } else return [...acc];
        }, []);

    if (chartData.length > 0) {
      const start = minX !== null ? minX : 0;
      const end = maxX !== null ? maxX + 1 : chartData.length;

      return chartData.slice(start, end).map((chartItem: any) => {
        const result = { ...chartItem };

        Object.keys(result).forEach((key: any) => {
          if (key.includes(EmissionCurvesType.CH4CO2E) || key.includes(EmissionCurvesType.CO2)) {
            result[key] = getValidatedValue(minCh4co2Y, maxCh4co2Y, chartItem[key]);
          }
          if (key.includes(EmissionCurvesType.N2OCO2E)) {
            result[key] = getValidatedValue(minNo2Y, maxNo2Y, chartItem[key]);
          }
        });

        return result;
      });
    }

    return [];
  },
);

export const selectIsChartDataEmpty = createSelector(
  [
    selectChartData,
    selectVisibleEmissionChartData,
    (state: RootState, widgetCurveType: WidgetCurveType) => widgetCurveType,
  ],
  (chartData, emissionsChartData, widgetCurveType) => {
    return widgetCurveType === WidgetCurveType.PRODUCTION ? !chartData.length : !emissionsChartData.length;
  },
);
export const selectIsVisibleCurvesEmpty = createSelector([selectLinesProps], (lineProps) => {
  return !lineProps.length;
});

export const selectMaxChartDataMonth = createSelector([selectChartData], (chartData) => {
  return getChartXAxisMaxValue(chartData);
});

export const selectMaxChartDataValue = createSelector([selectChartData], (chartData) => {
  const maxValue = getChartYAxisMaxValue(chartData);

  return Math.ceil(maxValue);
});

export const selectPlanProdCurvesRawData = createSelector([selectVisiblePlanProdCurvesWidgetDomain], (curvesData) => {
  const rawData: Record<string, PlanPredictCurvesResponsePayload[]> = {};

  for (const [wellId, value] of Object.entries(curvesData)) {
    if (value.status === RequestStatus.SUCCESS) {
      rawData[wellId] = value.prodCurves.rawData;
    }
  }

  return rawData;
});

export const selectEmissionCurvesRawData = createSelector([selectVisiblePlanProdCurvesWidgetDomain], (curvesData) => {
  const rawData: Record<string, PlanPredictCurvesResponsePayload[]> = {};

  for (const [wellId, value] of Object.entries(curvesData)) {
    if (value.status === RequestStatus.SUCCESS) {
      rawData[wellId] = value.emissionCurves.rawData;
    }
  }

  return rawData;
});
